import { BookText, ChartCandlestick, ListTodo } from 'lucide-react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Image } from '@/app/_components/common/Image';
import { ButtonV2 } from '@/app/_components/ui/ButtonV2';
import { cn } from '@/app/_lib/utils/utils';
import type { FeaturedHeroData } from '@/app_data/mock/ndrData';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import { trackGtmEvent } from '@/app_lib/utils/ndrUtils';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const featuredHeroVariants = tv({
  defaultVariants: {
    variant: 'default',
  },
  slots: {
    container: 'grid layout-gap-md md:grid-cols-2 w-full',
    contentSection: 'flex flex-col justify-center layout-gap-md',
    ctaButton: 'md:w-auto w-full',
    ctaSection: 'flex flex-col gap-md items-center justify-center md:items-start',
    featureContent: 'flex flex-col gap-1',
    featureDescription: 'text-default text-secondary',
    featureItem: 'flex gap-lg items-start',
    featuresContainer: 'flex flex-col layout-gap-xs',
    featureTitle: 'font-semibold typography-level-6',
    headerSection: 'flex flex-col gap-sm',
    icon: 'h-6 text-yellow-600 w-6',
    iconContainer:
      'bg-gradient-to-br from-[rgba(255,252,242,1)] flex h-11 items-center justify-center rounded-full to-[rgba(255,232,173,1)] w-11',
    image: 'h-auto w-full',
    imageSection: '',
    note: 'text-sm',
    subtitle: 'typography-level-6',
    title: 'font-semibold typography-level-2',
  },
});

interface FeaturedHeroProps extends VariantProps<typeof featuredHeroVariants> {
  className?: string;
  data: FeaturedHeroData;
  queryParams?: string;
}

const ICON_MAP: Record<string, React.ElementType> = {
  bookText: BookText,
  chartCandlestick: ChartCandlestick,
  listTodo: ListTodo,
} as const;

const FeaturedHero = ({ className = '', data, queryParams }: FeaturedHeroProps) => {
  const slots = featuredHeroVariants({});
  const { optimizely } = useOptimizely();

  const ctaUrl = `${data.cta.url}${queryParams ? `?${queryParams}` : ''}`;
  const handleCtaClick = (url: string) => {
    trackOptimizelyEvent(optimizely, 'ndr_compare_top_option_click', {});
    trackGtmEvent(url);
  };

  const headerSection = (
    <div className={slots.headerSection()}>
      <h2
        className={slots.title()}
        data-cy='ndr-featured-hero-title'
      >
        {data.title}
      </h2>
      <p
        className={slots.subtitle()}
        data-cy='ndr-featured-hero-description'
      >
        {data.subtitle}
      </p>
    </div>
  );

  const featuresSection = (
    <div className={slots.featuresContainer()}>
      {data.items.map((feature, index) => {
        const IconComponent = ICON_MAP[feature.iconName];

        if (!IconComponent) {
          return null;
        }

        return (
          <div
            className={slots.featureItem()}
            data-cy={`ndr-featured-hero-feature-${index}`}
            key={`${feature.iconName}-${index}`}
          >
            <div className={slots.iconContainer()}>
              <IconComponent
                aria-hidden='true'
                className={slots.icon()}
              />
            </div>
            <div className={slots.featureContent()}>
              <h3 className={slots.featureTitle()}>{feature.title}</h3>
              <p className={slots.featureDescription()}>{feature.subtitle}</p>
            </div>
          </div>
        );
      })}
    </div>
  );

  const contentSection = (
    <div className={slots.contentSection()}>
      {headerSection}
      {featuresSection}
      <div className={slots.ctaSection()}>
        <ButtonV2
          className={slots.ctaButton()}
          data-cy='ndr-featured-hero-primary-cta'
          href={ctaUrl}
          onClick={() => handleCtaClick(ctaUrl)}
          size='lg'
        >
          {data.cta.text}
        </ButtonV2>
        {data.note && (
          <span
            className={slots.note()}
            data-cy='ndr-hero-note'
          >
            {data.note}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <section className={cn(slots.container(), className)}>
      {contentSection}
      <div className={slots.imageSection()}>
        <Image
          alt='Featured Hero Image'
          className={slots.image()}
          fullWidth={false}
          height={400}
          priority
          sizes='(max-width: 768px) 100vw, 50vw'
          src={data.image}
          width={600}
        />
      </div>
    </section>
  );
};

export { FeaturedHero };
